<script lang="ts">
// One import block for both scripts: the compiler hoists this one to module
// scope, so `<script setup>` below reads from it (a second `from 'vue'` here
// would be the same module imported twice).
import { h, type PropType, type VNode } from 'vue';
import { NIcon, dropdownProps, type PopoverTrigger } from 'naive-ui';
import { IconCheck } from '@/components/design/icons';
import { gaiaStatus } from '../themes/tokens';

/**
 * Spread onto a destructive option — the menu-item counterpart of
 * GaiaDeleteButton, for the views that put "Delete" behind the row ⋯ instead
 * of on a trash button. naive colours a menu item through the option's own
 * `props.style`, so this can't be a theme override the way the button's danger
 * wash is; handing every call site the same object is what stops "Delete view"
 * being red while "Delete draft" stays black.
 */
export const gaiaDangerOption: { style: { color: string } } = {
  style: { color: gaiaStatus.alert },
};

/**
 * The tick an option wears while it is the one in effect, so a menu that
 * switches between things (a view, a page) doubles as the indicator for which
 * of them you are on. Returns `undefined` for the rest, which is what
 * `DropdownOption.icon` wants for "no glyph" — the option keeps the same
 * indent either way, so the list doesn't shift as the selection moves.
 */
export function gaiaCheckedIcon(checked: boolean): (() => VNode) | undefined {
  return checked ? (): VNode => h(NIcon, { component: IconCheck }) : undefined;
}
</script>

<script setup lang="ts">
const props = defineProps({
  ...dropdownProps,
  // Gaia menus open on click, not hover.
  trigger: {
    type: String as PropType<PopoverTrigger>,
    default: 'click',
  },
});
</script>

<template>
  <!-- Option-list menu (workspace switcher, row ⋯). For a panel of arbitrary
       content anchored to a control, use GaiaPopover instead. -->
  <n-dropdown v-bind="props">
    <slot />
  </n-dropdown>
</template>
